import type { BlogPost } from "@/types/content";

// Bone health articles (posts 26-32).
// Article images are managed centrally in src/data/blog/images.ts (keyed by slug).
export const bonePosts: BlogPost[] = [
  {
    id: 26,
    slug: "how-to-maintain-strong-and-healthy-bones",
    title: "How to Maintain Strong and Healthy Bones",
    category: "Bone Health",
    excerpt: "Bones are living tissue. Nutrition, activity and sunlight all play a part in keeping them strong.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Still%20life%20of%20calcium%20rich%20foods%20including%20leafy%20greens%20yoghurt%20and%20milk%20on%20a%20light%20neutral%20surface%2C%20soft%20daylight%2C%20clean%20wellness%20editorial%20photography%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-26&orientation=landscape&nocache=true",
    imageAlt: "Calcium rich foods that support strong bones",
    metaTitle: "How to Maintain Strong and Healthy Bones | Ortho Spine Care",
    metaDescription:
      "Learn the nutrition, activity and lifestyle habits that help keep bones strong as you age. Patient-friendly guidance from Ortho Spine Care, Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-01-10",
    updatedDate: "2026-06-11",
    readTime: "5 min read",
    tags: ["Bone Health", "Nutrition", "Prevention"],
    content: [
      {
        heading: "Bones are living tissue",
        paragraphs: [
          "Bone is continually broken down and rebuilt throughout life. As we get older, this balance can shift and bone strength can gradually decline, which is why consistent habits matter.",
        ],
      },
      {
        heading: "Habits that support bone strength",
        bullets: [
          "Eat enough calcium from milk, yoghurt, leafy greens and other sources",
          "Get sensible sunlight for vitamin D",
          "Stay active with weight-bearing exercise such as walking",
          "Include some strength and balance work",
          "Avoid smoking and limit excess alcohol",
        ],
      },
      {
        heading: "When to discuss bone health",
        paragraphs: [
          "If you have risk factors for weak bones, or have broken a bone from a minor fall, it is worth discussing bone health with your doctor. Early guidance can make a meaningful difference.",
        ],
      },
    ],
    faqs: [
      {
        q: "How much calcium do adults need?",
        a: "Requirements vary with age and sex. A balanced diet usually covers most needs, and your doctor can advise if a supplement is appropriate for you.",
      },
      {
        q: "Can exercise really strengthen bones?",
        a: "Yes. Weight-bearing and strengthening exercise helps maintain bone density and also improves balance and muscle strength.",
      },
    ],
    relatedServices: ["bone-deformities"],
    relatedConditions: ["fractures"],
  },
  {
    id: 27,
    slug: "common-causes-of-bone-pain",
    title: "Common Causes of Bone Pain",
    category: "Bone Health",
    excerpt: "Bone pain feels different from muscle or joint pain. Here are the causes worth knowing about.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Detailed%20three%20dimensional%20anatomical%20render%20of%20the%20bones%20of%20the%20lower%20leg%20on%20a%20clean%20light%20background%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-27&orientation=landscape&nocache=true",
    imageAlt: "Person experiencing bone pain in the leg",
    metaTitle: "Common Causes of Bone Pain | Ortho Spine Care",
    metaDescription:
      "Understand the common causes of bone pain and when it needs specialist assessment. Patient-friendly guidance from Ortho Spine Care in Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-01-17",
    updatedDate: "2026-06-13",
    readTime: "5 min read",
    tags: ["Bone Health", "Pain", "Diagnosis"],
    content: [
      {
        heading: "How bone pain differs",
        paragraphs: [
          "Bone pain is often described as a deep, dull ache that is felt inside the limb. It may be present at rest and can be worse at night, unlike typical muscle soreness.",
        ],
      },
      {
        heading: "Common causes",
        bullets: [
          "Injury or a fracture that has not been recognised",
          "Bone infection",
          "Bone tumours, which may be benign or malignant",
          "Nutritional deficiencies affecting bone",
          "Overuse-related stress injuries",
        ],
      },
      {
        heading: "Why it should be assessed",
        paragraphs: [
          "Because several of these causes benefit from early treatment, persistent bone pain should be evaluated rather than simply managed with painkillers.",
        ],
      },
    ],
    faqs: [
      {
        q: "When is bone pain an emergency?",
        a: "Severe bone pain with fever, together with redness or swelling, needs prompt attention as it may indicate infection.",
      },
      {
        q: "How is bone pain investigated?",
        a: "Assessment usually starts with examination and imaging such as an X-ray, with further tests arranged as needed.",
      },
    ],
    relatedServices: ["bone-tumours", "bone-infections"],
    relatedConditions: ["fractures", "bone-and-joint-infections"],
  },
  {
    id: 28,
    slug: "understanding-bone-and-joint-health",
    title: "Understanding Bone and Joint Health",
    category: "Bone Health",
    excerpt: "Bones and joints work as a system. Looking after both keeps you moving comfortably.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Clean%20anatomical%20model%20of%20a%20skeleton%20hand%20and%20knee%20joint%20on%20a%20white%20surface%20in%20a%20minimal%20study%2C%20soft%20daylight%2C%20premium%20editorial%20photography%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-28&orientation=landscape&nocache=true",
    imageAlt: "Anatomical models illustrating bone and joint health",
    metaTitle: "Understanding Bone and Joint Health | Ortho Spine Care",
    metaDescription:
      "How bones and joints work together, and the everyday habits that keep both healthy. Patient-friendly guidance from Ortho Spine Care in Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-01-24",
    updatedDate: "2026-06-17",
    readTime: "5 min read",
    tags: ["Bone Health", "Joint Health", "Wellness"],
    content: [
      {
        heading: "Bones and joints as a system",
        paragraphs: [
          "Bones give the body structure and store minerals, while joints allow movement where bones meet. Muscles, tendons and ligaments connect the two, so a problem in one area often affects the others.",
        ],
      },
      {
        heading: "What keeps the system healthy",
        bullets: [
          "Regular, varied movement",
          "Adequate calcium and vitamin D",
          "Strength work for muscles that support the joints",
          "A healthy body weight",
          "Avoiding smoking and excess alcohol",
        ],
      },
      {
        heading: "When to seek advice",
        paragraphs: [
          "Persistent pain, swelling, reduced movement or a bone that breaks from a minor injury are all reasons to seek an assessment rather than waiting.",
        ],
      },
    ],
    faqs: [
      {
        q: "Does weak bone always cause joint pain?",
        a: "Not necessarily, but bone and joint conditions often occur together with age. A specialist can identify what is contributing.",
      },
      {
        q: "Can I improve bone and joint health at any age?",
        a: "Yes. While the greatest gains are earlier in life, activity and nutrition continue to benefit bones and joints at every age.",
      },
    ],
    relatedServices: ["bone-deformities", "joint-disorders"],
    relatedConditions: ["arthritis", "fractures"],
  },
  {
    id: 29,
    slug: "fractures-types-symptoms-and-treatment",
    title: "Fractures: Types, Symptoms and Treatment",
    category: "Bone Health",
    excerpt: "A fracture is a break in the bone. Understanding the type helps explain the treatment.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=X-ray%20image%20of%20a%20fractured%20bone%20on%20a%20light%20viewing%20box%20in%20a%20clean%20medical%20setting%2C%20soft%20daylight%2C%20professional%20editorial%20photography%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-29&orientation=landscape&nocache=true",
    imageAlt: "X-ray showing a bone fracture",
    metaTitle: "Fractures: Types, Symptoms and Treatment | Ortho Spine Care",
    metaDescription:
      "Learn about the different types of fractures, their symptoms and treatment options, including fixation. Patient-friendly guidance from Ortho Spine Care, Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-01-31",
    updatedDate: "2026-06-20",
    readTime: "6 min read",
    tags: ["Fractures", "Bone Health", "Treatment"],
    content: [
      {
        heading: "Types of fracture",
        bullets: [
          "Simple fracture, where the bone is broken but the skin is intact",
          "Open fracture, where the broken bone breaks the skin",
          "Comminuted fracture, with several bone fragments",
          "Stress fracture, from repeated overuse",
        ],
      },
      {
        heading: "Symptoms",
        bullets: [
          "Severe pain at the site of injury",
          "Swelling, bruising and sometimes deformity",
          "Difficulty or inability to use the limb",
          "A visible break in the skin in open fractures",
        ],
      },
      {
        heading: "Treatment options",
        paragraphs: [
          "Simple fractures may be treated with a cast or brace. Unstable or complex fractures often need surgical fixation to hold the bone in position while it heals, followed by rehabilitation.",
        ],
      },
    ],
    faqs: [
      {
        q: "How long does a fracture take to heal?",
        a: "Healing time varies with the bone, the severity of the fracture and your general health. Your specialist will monitor progress and guide your recovery.",
      },
      {
        q: "Are metal implants always removed?",
        a: "Often implants stay in place permanently, but sometimes they are removed later. Your specialist will advise based on your fracture and progress.",
      },
    ],
    relatedServices: ["complex-bone-fracture-management"],
    relatedConditions: ["fractures"],
  },
  {
    id: 30,
    slug: "what-to-do-after-a-bone-injury",
    title: "What to Do After a Bone Injury",
    category: "Bone Health",
    excerpt: "The first steps after a bone injury affect recovery. Here is a sensible approach.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Supportive%20arm%20sling%20and%20bandage%20on%20a%20clean%20soft%20surface%20in%20a%20bright%20room%2C%20gentle%20daylight%2C%20first%20aid%20editorial%20photography%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-30&orientation=landscape&nocache=true",
    imageAlt: "Support and bandaging after a bone injury",
    metaTitle: "What to Do After a Bone Injury | Ortho Spine Care",
    metaDescription:
      "Practical first steps after a bone injury, and the signs that mean you should get it checked. Patient guidance from Ortho Spine Care in Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-02-07",
    updatedDate: "2026-06-22",
    readTime: "4 min read",
    tags: ["Bone Health", "First Aid", "Fractures"],
    content: [
      {
        heading: "Immediate steps after an injury",
        bullets: [
          "Keep the limb still and supported",
          "Avoid trying to straighten a deformed limb",
          "Apply a cold pack for short periods if there is swelling",
          "Get medical assessment if you cannot bear weight or move the limb",
        ],
      },
      {
        heading: "Why early assessment matters",
        paragraphs: [
          "Some fractures are not obvious from the outside. Early assessment and imaging confirm whether the bone is broken and ensure it is treated in a good position.",
        ],
      },
      {
        heading: "What recovery involves",
        paragraphs: [
          "Depending on the injury, recovery may involve a cast, brace or surgical fixation, together with physiotherapy to restore movement and strength. Following the plan closely helps you return to activity safely.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can I walk on a suspected fracture?",
        a: "Do not force weight through a limb that is painful or deformed. Keep it supported and get it assessed.",
      },
      {
        q: "Is swelling normal after a bone injury?",
        a: "Some swelling is expected, but rapid or severe swelling, or swelling with fever, should be assessed promptly.",
      },
    ],
    relatedServices: ["complex-bone-fracture-management"],
    relatedConditions: ["fractures"],
  },
  {
    id: 31,
    slug: "bone-health-and-healthy-lifestyle-habits",
    title: "Bone Health and Healthy Lifestyle Habits",
    category: "Bone Health",
    excerpt: "Small daily choices add up over decades. Here is what actually supports your bones.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Detailed%20three%20dimensional%20anatomical%20render%20of%20healthy%20human%20bone%20structure%20on%20a%20clean%20light%20background%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-31&orientation=landscape&nocache=true",
    imageAlt: "Morning walk in sunlight supporting bone health",
    metaTitle: "Bone Health and Healthy Lifestyle Habits | Ortho Spine Care",
    metaDescription:
      "Discover the lifestyle habits that support bone health, from activity and sunlight to avoiding smoking. Guidance from Ortho Spine Care in Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-02-14",
    updatedDate: "2026-06-24",
    readTime: "5 min read",
    tags: ["Bone Health", "Lifestyle", "Prevention"],
    content: [
      {
        heading: "The habits that matter",
        bullets: [
          "Move regularly, including weight-bearing activity",
          "Get sensible sun exposure for vitamin D",
          "Eat a balanced diet with enough calcium",
          "Keep alcohol within sensible limits",
          "Do not smoke",
        ],
      },
      {
        heading: "Why consistency beats intensity",
        paragraphs: [
          "Bone responds to regular load over time. A steady routine of walking, activity and good nutrition generally supports bone health more reliably than occasional bursts of effort.",
        ],
      },
      {
        heading: "Extra care for higher risk groups",
        paragraphs: [
          "People with a family history of fractures, long-term steroid use, or certain medical conditions may need extra attention to bone health. A doctor can advise on assessment where appropriate.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is sunlight necessary for bone health?",
        a: "Sunlight helps the body make vitamin D, which is needed to absorb calcium. Short, sensible exposure is enough for most people.",
      },
      {
        q: "Does smoking affect bones?",
        a: "Yes. Smoking is associated with reduced bone strength and slower healing, so stopping is beneficial for bone health.",
      },
    ],
    relatedServices: ["bone-deformities"],
    relatedConditions: ["fractures"],
  },
  {
    id: 32,
    slug: "when-to-consult-an-orthopaedic-doctor-for-bone-pain",
    title: "When Should You Consult an Orthopaedic Doctor for Bone Pain?",
    category: "Bone Health",
    excerpt: "Not all bone pain needs a specialist — but these signs mean it is time to get it checked.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=A%20bone%20X-ray%20film%20on%20a%20lightbox%20beside%20an%20anatomical%20bone%20model%20on%20a%20clean%20desk%20in%20a%20bright%20clinic%20with%20no%20people%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-32&orientation=landscape&nocache=true",
    imageAlt: "Patient consulting an orthopaedic doctor about bone pain",
    metaTitle: "When to See an Orthopaedic Doctor for Bone Pain | Ortho Spine Care",
    metaDescription:
      "Learn when bone pain needs an orthopaedic specialist, and what to expect at your consultation. Clear guidance from Ortho Spine Care in Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-02-21",
    updatedDate: "2026-06-26",
    readTime: "4 min read",
    tags: ["Bone Health", "Orthopaedic", "Patient Education"],
    content: [
      {
        heading: "Signs that need a specialist opinion",
        bullets: [
          "Bone pain that persists for more than a few weeks",
          "Pain that is worse at night or wakes you from sleep",
          "Pain with fever, redness or swelling",
          "An unexplained lump or swelling over a bone",
          "A fracture following a minor injury",
        ],
      },
      {
        heading: "What the assessment involves",
        paragraphs: [
          "Your doctor will take a history, examine the area, and arrange imaging such as an X-ray. Further tests may be needed depending on what is found.",
        ],
      },
      {
        heading: "Why early assessment helps",
        paragraphs: [
          "Several causes of bone pain respond well to early treatment. Assessment also provides reassurance when the cause turns out to be minor.",
        ],
      },
    ],
    faqs: [
      {
        q: "Should I take painkillers before seeing a doctor?",
        a: "Simple pain relief may help in the short term, but it should not delay assessment of persistent or unexplained bone pain.",
      },
      {
        q: "Do I need a scan for bone pain?",
        a: "Only if it is likely to change the treatment plan. Your doctor will decide which tests are appropriate.",
      },
    ],
    relatedServices: ["bone-tumours", "bone-infections", "complex-bone-fracture-management"],
    relatedConditions: ["fractures", "bone-and-joint-infections"],
  },
];